import type { Cell, Graph } from '@maxgraph/core';
import { Geometry } from '@maxgraph/core';
import type { WorkflowGraph } from '../WorkflowGraph';
import type {
  GraphSnapshot,
  MaterialNodeSpec,
  OperationNodeSpec,
  FlowEdgeSpec,
  GroupSpec,
} from './types';
import { classify } from './classify';
import { collectMemberCells } from './group';
import { normalizeLayoutManagedEdges } from './edgeGeometry';
import {
  MATERIAL_STYLE,
  MATERIAL_REMAINDER_STYLE,
  MATERIAL_DIMENSIONS,
  COMPLEX_OP_STYLE,
  COMPLEX_OP_DIMENSIONS,
  LINK_EDGE_STYLE,
} from '../styles';

const GROUP_PADDING = 20;

/**
 * maxGraph silently auto-renames cells whose id collides with an existing one
 * (GraphDataModel.cellAdded) rather than rejecting them. That turns a snapshot
 * carrying duplicate ids — the signature of an unfiltered batched run graph —
 * into a pile of stacked, cross-wired cells with no error anywhere. Make it
 * loud instead: callers should narrow the snapshot first (see domain/batches).
 *
 * Cheap (one pass over the snapshot, dwarfed by hydration itself) and always
 * worth surfacing, so it is not gated behind a dev flag.
 */
function warnOnDuplicateIds(snapshot: GraphSnapshot): void {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  const check = (id: string | undefined) => {
    if (!id) return;
    if (seen.has(id)) duplicates.add(id);
    else seen.add(id);
  };

  snapshot.materialNodes.forEach((n) => check(n.id));
  snapshot.operationNodes.forEach((n) => check(n.id));
  snapshot.groups.forEach((g) => check(g.id));
  // Edge ids live in their own namespace — check them separately.
  const seenEdges = new Set<string>();
  for (const edge of snapshot.flowEdges) {
    if (!edge.id) continue;
    if (seenEdges.has(edge.id)) duplicates.add(edge.id);
    else seenEdges.add(edge.id);
  }

  if (duplicates.size > 0) {
    console.error(
      `[hydrateFromSnapshot] Snapshot contains ${duplicates.size} duplicate id(s); ` +
        `maxGraph will silently rename the collisions and the graph will render ` +
        `incorrectly. Is this a batched run graph that was not filtered by ` +
        `cloneNumber? Example ids:`,
      [...duplicates].slice(0, 5),
    );
  }
}

/**
 * Clear all cells from the graph (vertices and edges).
 */
export function clearGraph(graph: Graph): void {
  graph.batchUpdate(() => {
    const parent = graph.getDefaultParent();
    graph.removeCells(graph.getChildCells(parent, true, true), true);
  });
}

/**
 * Hydrate a graph from a snapshot. Clears existing content first,
 * then inserts materials → COMPLEX ops → flow edges → groups in dependency order.
 *
 * Everything runs inside a single batchUpdate so LayoutManager fires once.
 */
export function hydrateFromSnapshot(
  graph: Graph,
  snapshot: GraphSnapshot,
): void {
  const model = graph.getDataModel();
  const parent = graph.getDefaultParent();

  warnOnDuplicateIds(snapshot);

  // Build a lookup for operation nodes by ID for edge processing
  const opLookup = new Map<string, OperationNodeSpec>(
    snapshot.operationNodes
      .filter((op): op is OperationNodeSpec & { id: string } => !!op.id)
      .map((op) => [op.id, op]),
  );

  graph.batchUpdate(() => {
    // Step A: clear all cells
    const children = graph.getChildCells(parent, true, true);
    for (const child of children) {
      model.remove(child);
    }

    // Step B: insert material vertices
    for (const mat of snapshot.materialNodes) {
      if (!mat.id) continue;
      const style = mat.isReminder ? MATERIAL_REMAINDER_STYLE : MATERIAL_STYLE;
      graph.insertVertex({
        id: mat.id,
        parent,
        value: {
          kind: 'material' as const,
          label: `${mat.name}`,
          id: mat.id,
          name: mat.name,
          isEmpty: mat.isEmpty ?? false,
          isReminder: mat.isReminder ?? false,
          createdAt: mat.createdAt ?? new Date().toISOString(),
        },
        position: [mat.position?.x ?? 0, mat.position?.y ?? 0],
        size: [MATERIAL_DIMENSIONS.width, MATERIAL_DIMENSIONS.height],
        style,
      });
    }

    // Step C: insert COMPLEX operation vertices only
    for (const op of snapshot.operationNodes) {
      if (op.archetype !== 'COMPLEX' || !op.id) continue;
      graph.insertVertex({
        id: op.id,
        parent,
        value: { kind: 'operation' as const, label: op.displayName, id: op.id },
        position: [op.position?.x ?? 0, op.position?.y ?? 0],
        size: [COMPLEX_OP_DIMENSIONS.width, COMPLEX_OP_DIMENSIONS.height],
        style: COMPLEX_OP_STYLE,
      });
    }

    // Step D: insert flow edges via the pending-half mechanism.
    // This correctly collapses two store edges into one visual edge
    // for non-COMPLEX operations (ops-as-edges pattern).
    // Link edges are inserted directly as dashed red edges, not collapsed.
    const wg = graph as unknown as WorkflowGraph;
    for (const edge of snapshot.flowEdges) {
      if (!edge.id) continue;
      
      if (edge.edgeKind === 'graphsLink') {
        // Link edges: insert directly as visual edges with LINK_EDGE_STYLE
        const sourceCell = model.getCell(edge.sourceId);
        const targetCell = model.getCell(edge.targetId);
        
        if (!sourceCell || !targetCell) {
          continue;
        }
        
        const linkEdge = graph.insertEdge({
          id: edge.id,
          parent,
          source: sourceCell,
          target: targetCell,
          value: '', // Empty string to avoid "[object object]" label
          style: LINK_EDGE_STYLE,
        });
        
        // Store edgeKind in custom property for persistence
        (linkEdge as any).edgeKind = 'graphsLink';
        continue;
      }
      
      const sourceIsOp = opLookup.has(edge.sourceId);
      if (!sourceIsOp) {
        // material → operation
        const op = opLookup.get(edge.targetId);
        const displayName = op?.displayName ?? '';
        const archetype = op?.archetype;
        wg.connectMaterialToOperation(edge.sourceId, edge.targetId, edge.id, displayName, archetype);
      } else {
        // operation → material
        const op = opLookup.get(edge.sourceId);
        const displayName = op?.displayName ?? '';
        const archetype = op?.archetype;
        wg.connectOperationToMaterial(edge.sourceId, edge.targetId, edge.id, displayName, archetype);
      }
    }

    // Step D2: fix TRANSFER visual edges — the _pendingEdgeHalf mechanism only
    // supports 1-in/1-out and produces a wrong edge for TRANSFER (2-in/2-out).
    // Rebuild correct visual edges for every TRANSFER operation.
    for (const op of snapshot.operationNodes) {
      if (op.archetype !== 'TRANSFER' || !op.id) continue;

      const inputs  = snapshot.flowEdges.filter((e) => e.targetId === op.id);
      const outputs = snapshot.flowEdges.filter((e) => e.sourceId === op.id);

      // Prefer explicit transferRow IDs when present. Flow edge ordering is not
      // guaranteed across persisted snapshots; relying on outputs[0]/outputs[1]
      // can swap remainder and destination post-state in analysis rendering.
      const transferRow = op.transferRow;

      const inputSourceIds = new Set(inputs.map((e) => e.sourceId));
      const outputTargetIds = new Set(outputs.map((e) => e.targetId));

      const srcPreFromRow =
        transferRow?.sourceMaterialId && inputSourceIds.has(transferRow.sourceMaterialId)
          ? transferRow.sourceMaterialId
          : null;
      const dstPreFromRow =
        transferRow?.destinationMaterialId && inputSourceIds.has(transferRow.destinationMaterialId)
          ? transferRow.destinationMaterialId
          : null;
      const srcPostFromRow =
        transferRow?.sourcePostMaterialId && outputTargetIds.has(transferRow.sourcePostMaterialId)
          ? transferRow.sourcePostMaterialId
          : null;
      const dstPostFromRow =
        transferRow?.destinationPostMaterialId && outputTargetIds.has(transferRow.destinationPostMaterialId)
          ? transferRow.destinationPostMaterialId
          : null;

      const srcPre = srcPreFromRow ?? inputs[0]?.sourceId ?? null;
      const dstPre =
        dstPreFromRow ??
        (inputs.find((e) => e.sourceId !== srcPre)?.sourceId ?? null);

      const srcPost = srcPostFromRow ?? outputs[0]?.targetId ?? null;
      const dstPost =
        dstPostFromRow ??
        (outputs.find((e) => e.targetId !== srcPost)?.targetId ?? null);

      if (srcPre && srcPost && dstPost) {
        wg.addTransferVisualEdges({
          operationId: op.id,
          srcPre,
          dstPre,
          srcPost,
          dstPost,
          displayName: op.displayName,
        });
      }
    }

    // Step E: create groups — MUST be after all vertices and edges exist.
    // Membership uses the shared topology rule (collectMemberCells): every
    // material connected as input OR output to a group op, plus COMPLEX op
    // vertices. Boundary materials are included so the swimlane fully
    // encloses the step's I/O.
    //
    // Groups are FLAT by contract: GroupSpec carries no parent, and toSnapshot
    // only walks one level into a swimlane — so a group nested inside another
    // group loses its own children on the next save.
    //
    // Two maxGraph behaviours conspire to nest them anyway (GroupingMixin):
    //   1. groupCells() falls back to `cells[0].getParent()` when the group
    //      cell is detached, so the swimlane lands wherever its first member
    //      happens to live.
    //   2. getCellsForGroup(), which would restrict members to a single
    //      parent, is unreachable when `cells` is supplied by the caller.
    // Merging one group's leaf into another group's root makes that material a
    // member of BOTH groups; the group hydrated first adopts it, and the second
    // group is then created *inside* the first.
    //
    // Guard on both axes below: anchor every group cell to the default parent
    // before grouping, and let the group whose step PRODUCES a shared boundary
    // material keep it (the consuming group treats it as external, which is the
    // same handling an ungrouped upstream material already gets).
    const groupIdByStepId = new Map<string, string>();
    for (const g of snapshot.groups) {
      if (!g.id) continue;
      for (const stepId of g.stepIds ?? []) groupIdByStepId.set(stepId, g.id);
    }

    const producingGroupByMaterial = new Map<string, string>();
    for (const edge of snapshot.flowEdges) {
      if (edge.edgeKind === 'graphsLink') continue;
      // operation → material only; the source group produces the target.
      if (!opLookup.has(edge.sourceId)) continue;
      const ownerGroupId = groupIdByStepId.get(edge.sourceId);
      if (ownerGroupId) producingGroupByMaterial.set(edge.targetId, ownerGroupId);
    }

    for (const group of snapshot.groups) {
      if (!group.id) continue;

      console.log(`[hydrateFromSnapshot] Loading group ${group.id} (${group.label}), stepIds:`, group.stepIds);
      console.log(`[hydrateFromSnapshot] group.stepIds length:`, group.stepIds?.length);
      console.log(`[hydrateFromSnapshot] Full group object from snapshot:`, group);

      const allMembers = collectMemberCells(graph, group.stepIds);

      // Drop boundary materials that another group produces — they stay in the
      // producing swimlane and connect across the border. Only applied while
      // enough members remain to still form a swimlane, so a group is never
      // dissolved by the filter.
      const ownedMembers = allMembers.filter((cell) => {
        const owner = producingGroupByMaterial.get(cell.getId() ?? '');
        return !owner || owner === group.id;
      });
      const memberCells = ownedMembers;

      console.log(`[hydrateFromSnapshot] After collectMemberCells: got ${memberCells.length} cells`);

      // maxGraph's groupCells() no-ops on a single cell, so anything below two
      // members cannot become a swimlane.
      if (memberCells.length <= 1) {
        console.warn(`[hydrateFromSnapshot] WARNING: Only ${memberCells.length} member(s) collected for group ${group.id}, skipping`);
        continue;
      }

      // Store original positions so we can restore relative coords after reparenting
      const memberPositions = memberCells.map((cell) => {
        const geo = cell.getGeometry();
        return { cell, originalGeo: geo ? { x: geo.x, y: geo.y, width: geo.width, height: geo.height } : null };
      });

      // Access _pendingGroupSpec on WorkflowGraph to configure the group cell
      // created by createGroupCell override during groupCells().
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const wg = graph as any;
      wg._pendingGroupSpec = { id: group.id, label: group.label };
      // Build the swimlane and anchor it to the default parent BEFORE grouping.
      // Passing it in (rather than null) makes groupCells use group.getParent()
      // instead of inferring the parent from cells[0] — which is what nests one
      // group inside another when they share a boundary material.
      const groupCell = wg.createGroupCell(memberCells) as Cell;
      model.add(parent, groupCell, parent.getChildCount());
      graph.groupCells(groupCell, GROUP_PADDING, memberCells);
      wg._pendingGroupSpec = null;

      if (groupCell) {
        const geoAfterGroup = groupCell.getGeometry()!;

        // CRITICAL FIX: Restore member positions as RELATIVE to swimlane (not absolute canvas coords)
        // maxGraph needs relative coordinates for children of swimlanes
        graph.batchUpdate(() => {
          for (const { cell, originalGeo } of memberPositions) {
            if (originalGeo) {
              const relX = originalGeo.x - geoAfterGroup.x;
              const relY = originalGeo.y - geoAfterGroup.y;
              const newGeo = new Geometry(relX, relY, originalGeo.width, originalGeo.height);
              model.setGeometry(cell, newGeo);
            }
          }
        });
        
        graph.updateAlternateBounds(groupCell, geoAfterGroup, true);
        const geoAfterBounds = groupCell.getGeometry()!;
        model.setGeometry(groupCell, geoAfterBounds);

        if (group.isCollapsed) {
          graph.foldCells(true, false, [groupCell]);
        }
      }
    }
  });
}

/**
 * Serialize the current graph state into a portable snapshot.
 */
export function toSnapshot(graph: Graph): GraphSnapshot {
  const parent = graph.getDefaultParent();

  // Ensure persisted snapshots do not carry curve-driving geometry/state.
  graph.batchUpdate(() => {
    normalizeLayoutManagedEdges(graph, parent);
  });

  const materialNodes: MaterialNodeSpec[] = [];
  const operationNodes: OperationNodeSpec[] = [];
  const flowEdges: FlowEdgeSpec[] = [];
  const groups: GroupSpec[] = [];

  // Also collect children of group cells (swimlanes)
  const allVertices = graph.getChildCells(parent, true, false);
  const groupChildren: ReturnType<typeof graph.getChildCells> = [];
  for (const v of allVertices) {
    if (classify(v) === 'group') {
      groupChildren.push(...graph.getChildCells(v, true, false));
    }
  }
  const vertices = [...allVertices, ...groupChildren];
  const edges = graph.getChildCells(parent, false, true);

  // Also gather edges inside groups
  for (const v of allVertices) {
    if (classify(v) === 'group') {
      edges.push(...graph.getChildCells(v, false, true));
    }
  }

  const seen = new Set<string>();

  for (const cell of vertices) {
    const val = cell.getValue();
    const id = cell.getId();
    if (!val || !id || seen.has(id)) continue;
    seen.add(id);

    const geo = cell.getGeometry();
    const kind = classify(cell);

    if (kind === 'material') {
      materialNodes.push({
        ...val,
        id,
        position: geo ? { x: geo.x, y: geo.y } : undefined,
      });
    } else if (kind === 'operation') {
      operationNodes.push({
        ...val,
        id,
        position: geo ? { x: geo.x, y: geo.y } : undefined,
      });
    } else if (kind === 'group') {
      groups.push({
        ...val,
        id,
        position: geo ? { x: geo.x, y: geo.y } : undefined,
        size: geo ? { width: geo.width, height: geo.height } : undefined,
      });
    }
  }

  for (const cell of edges) {
    const id = cell.getId();
    if (!id) {
      continue;
    }
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);

    const source = cell.getTerminal(true);
    const target = cell.getTerminal(false);
    
    if (source?.getId() && target?.getId()) {
      // Check for edgeKind either in cell's custom property or in cell value for backward compatibility
      let edgeKind: 'flow' | 'graphsLink' = 'flow';
      
      // First check custom property (current approach)
      if ((cell as any).edgeKind === 'graphsLink') {
        edgeKind = 'graphsLink';
      }
      // Then check cell value for backward compatibility
      else {
        const cellValue = cell.getValue();
        if (cellValue && typeof cellValue === 'object' && 'edgeKind' in cellValue) {
          edgeKind = cellValue.edgeKind as 'flow' | 'graphsLink';
        }
      }
      
      flowEdges.push({
        id,
        sourceId: source.getId()!,
        targetId: target.getId()!,
        edgeKind,
      });
    }
  }

  return {
    materialNodes,
    operationNodes,
    flowEdges,
    groups,
    schemaVersion: '2.0',
  };
}
